import { useEffect, useState } from "react";
import { Save, History, Trash2, RotateCcw, Clock, HardDrive, AlertCircle, Lock, FileText, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSnapshotStore } from "@/stores/snapshotStore";
import { CreateSnapshotDialog } from "./CreateSnapshotDialog";
import { RestoreDialog } from "./RestoreDialog";
import { formatDistanceToNow } from "date-fns";

export function SnapshotManager() {
  const { 
    snapshots, 
    isLoading, 
    loadSnapshots, 
    deleteSnapshot, 
    lastAutoSave, 
    autoSaveEnabled,
    storageUsed,
    fileStorageUsed,
    snapshotStorageUsed,
    storageLimit,
    storagePercentage
  } = useSnapshotStore();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<any>(null);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  const handleRestore = (snapshot: any) => {
    setSelectedSnapshot(snapshot);
    setRestoreDialogOpen(true);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'manual': return 'default';
      case 'auto': return 'secondary';
      case 'pre-restore': return 'outline';
      case 'pre-import': return 'outline';
      default: return 'default';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Checkpoints & Backups</h2>
          <p className="text-muted-foreground">
            Save and restore your data to any point in time
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} disabled={storagePercentage >= 100}>
          <Save className="h-4 w-4 mr-2" />
          Save Checkpoint
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Storage Usage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Used</span>
              <span className="font-medium">
                {formatBytes(storageUsed)} / {formatBytes(storageLimit)}
              </span>
            </div>
            <Progress 
              value={storagePercentage} 
              className={
                storagePercentage > 90 
                  ? "bg-red-100 [&>div]:bg-red-500" 
                  : storagePercentage > 75 
                  ? "bg-yellow-100 [&>div]:bg-yellow-500"
                  : ""
              }
            />
            <p className="text-xs text-muted-foreground">
              {storagePercentage.toFixed(1)}% of storage limit used
            </p>
          </div>

          {/* Storage breakdown */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Files (Evidence)</span>
              </div>
              <span className="font-medium">{formatBytes(fileStorageUsed)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Checkpoints</span>
              </div>
              <span className="font-medium">{formatBytes(snapshotStorageUsed)}</span>
            </div>
          </div>

          {storagePercentage > 90 && (
            <Alert variant="danger">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Storage almost full! Delete old snapshots to free up space.
              </AlertDescription>
            </Alert>
          )}

          {storagePercentage > 75 && storagePercentage <= 90 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Storage usage is high. Consider deleting old snapshots.
              </AlertDescription>
            </Alert>
          )}

          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Lock className="h-4 w-4" />
              <span>File Version Control (Coming Soon)</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Backup and version file contents with configurable storage quotas
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Auto-Save Status
          </CardTitle>
          <CardDescription>
            Automatic daily backups keep your data safe
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant={autoSaveEnabled ? "default" : "secondary"}>
                  {autoSaveEnabled ? "Enabled" : "Disabled"}
                </Badge>
                {lastAutoSave && (
                  <span className="text-sm text-muted-foreground">
                    Last save: {formatDistanceToNow(lastAutoSave, { addSuffix: true })}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Keeping last 7 daily backups
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Checkpoint History
          </CardTitle>
          <CardDescription>
            {snapshots.length} checkpoint{snapshots.length !== 1 ? 's' : ''} saved
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading checkpoints...
            </div>
          ) : snapshots.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No checkpoints yet. Create your first one!
            </div>
          ) : (
            <div className="space-y-3">
              {snapshots.map((snapshot) => (
                <div
                  key={snapshot.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">
                        {snapshot.name}
                        <span className="text-muted-foreground font-normal ml-2">
                          · {formatDistanceToNow(new Date(snapshot.created_at), { addSuffix: true })}
                        </span>
                      </h4>
                      <Badge variant={getTypeColor(snapshot.snapshot_type)}>
                        {snapshot.snapshot_type}
                      </Badge>
                    </div>
                    {snapshot.description && (
                      <p className="text-sm text-muted-foreground">
                        {snapshot.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>
                        {Object.values(snapshot.record_counts || {}).reduce((sum: number, count: any) => sum + (Number(count) || 0), 0)} records
                      </span>
                      <span>{formatBytes(snapshot.size_bytes || 0)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(snapshot)}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restore
                    </Button>
                    {snapshot.snapshot_type !== 'pre-restore' && snapshot.snapshot_type !== 'auto' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteSnapshot(snapshot.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateSnapshotDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      <RestoreDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        snapshot={selectedSnapshot}
      />
    </div>
  );
}
